import React, { useEffect, useState } from "react"
import styled from "styled-components"

interface Jeongeup {
  curDt: string
  curGen: number
  curUsg: number
  site: string // API에서는 "4", "6" 형태로 옴
}

interface Props {
  site: string // props에서는 "4동", "6동", "전체"
  year: string
}

interface GroupedData {
  번호: string
  구분: string
  월별: Record<string, number>
}

const Wrapper = styled.div`
  display: flex;
  height: 296px;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid var(--base-gray20, #dedede);
  background: var(--base-white50, rgba(255, 255, 255, 0.5));
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`

const HeaderCell = styled.div`
  flex: 1;
  width: 66.66px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border: 1px solid var(--base-gray10, #e8e8e8);
  background: var(--secondary10, #eef1f9);
  color: var(--base-gray100, #262626);
  text-align: center;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
`

const Row = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`

const Cell = styled.div`
  flex: 1;
  width: 66.66px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--base-gray20, #e8e8e8);
  color: var(--base-gray90, #404040);
  text-align: center;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`

const MonitoringTable: React.FC<Props> = ({ site, year }) => {
  const [data, setData] = useState<Record<string, GroupedData>>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchMonitoringData = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/jeongeup/monthly-data/`
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // 연도 필터링
        let filteredData = result.data.filter((v: Jeongeup) =>
          v.curDt.startsWith(year)
        )

        // API의 site 값 변환 ("4" -> "4동", "6" -> "6동")
        filteredData = filteredData.map((item: any) => ({
          ...item,
          site: `${item.site}동`,
        }))

        // 특정 site만 필터링
        if (site !== "전체") {
          filteredData = filteredData.filter((v: Jeongeup) => v.site === site)
        }

        // 데이터 그룹화
        const grouped = filteredData.reduce(
          (acc: Record<string, GroupedData>, item: any) => {
            const month = item.curDt.slice(-2) // "MM" 추출

            if (!acc[item.site]) {
              acc[item.site] = { 번호: item.site, 구분: "전력(Wh)", 월별: {} }
            }

            acc[item.site].월별[`${parseInt(month, 10)}월`] = item.curUsg // 월별 데이터 할당

            return acc
          },
          {}
        )

        setData(grouped)
      } catch (error) {
        setError(error instanceof Error ? error.message : "Unknown error")
      } finally {
        setLoading(false)
      }
    }

    fetchMonitoringData()
  }, [site, year])

  return (
    <Wrapper>
      <Header>
        <HeaderCell>번호</HeaderCell>
        <HeaderCell>구분</HeaderCell>
        {Array.from({ length: 12 }, (_, i) => (
          <HeaderCell key={i}>{`${i + 1}월`}</HeaderCell>
        ))}
      </Header>
      {loading && <h1 style={{ height: "141px" }}>Loading...</h1>}
      {error && <p>Error: {error}</p>}
      {!loading &&
        !error &&
        Object.values(data).map((row, index) => (
          <Row key={index}>
            <Cell>{row.번호}</Cell>
            <Cell>{row.구분}</Cell>
            {Array.from({ length: 12 }, (_, i) => (
              <Cell key={i}>
                {Number(
                  Number(row.월별[`${i + 1}월`] || 0).toFixed(0)
                ).toLocaleString() || 0}
              </Cell>
            ))}
          </Row>
        ))}
    </Wrapper>
  )
}

export default MonitoringTable
